import {numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {MONTH_POLISH_NAMES} from "../../types/month_polish_names";
import {ILocationGenericSection, ILocationGenericSectionProps, ParagraphType} from "./constants";

export const studioOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const now = new Date();
    const currentMonth = MONTH_POLISH_NAMES[now.getMonth()];
    const currentYear = now.getFullYear();

    const variables = {
        regionName: props.region.name,
        regionNameDeclensionWhere: props.region.name_declension_where,
        regionPropertiesCountForSale: props.region.stats?.properties_all_count_for_sale || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        studioPriceMin: props.region.stats?.properties_studios_ranges_price_min || 0,
        studiosRangesPriceM2Avg: props.region.stats?.properties_studios_ranges_price_m2_avg || 0,
        studiosCountReady: props.region.stats?.properties_studios_count_ready || 0,
        studiosCountReadyInThreeMonths: props.region.stats?.properties_studios_count_ready_in_3m || 0,
        studiosCountReadyInSixMonths: props.region.stats?.properties_studios_count_ready_in_6m || 0,
        studiosCountReadyInOneYear: props.region.stats?.properties_studios_count_ready_in_12m || 0,
        studiosGroundFloor: props.region.stats?.properties_studios_ground_floor || 0,
        studiosGroundFloorWithGarden: props.region.stats?.properties_studios_ground_floor_with_garden || 0,
        studiosHighFloor: props.region.stats?.properties_studios_high_floor || 0,
        studiosLastFloor: props.region.stats?.properties_studios_last_floor || 0,
        studiosLowFloor: props.region.stats?.properties_studios_low_floor || 0,
        studiosOffersCountForSale: props.region.stats?.offers_studios_count_for_sale || 0,
        studiosPropertiesCountForSale: props.region.stats?.properties_studios_count_for_sale || 0,
        studiosAreaMin: props.region.stats?.properties_studios_ranges_area_min || 0,
        studiosAreaMax: props.region.stats?.properties_studios_ranges_area_max || 0
    };

    if (!variables.studiosOffersCountForSale || !variables.studiosPropertiesCountForSale) {
        return [];
    }

    const studiosPercentage =
        variables.regionPropertiesCountForSale > 0 ? Math.round((variables.studiosPropertiesCountForSale / variables.regionPropertiesCountForSale) * 100) : 0;

    const areaText =
        variables.studiosAreaMin === variables.studiosAreaMax || variables.studiosAreaMax === 0
            ? `${variables.studiosAreaMin} mkw.`
            : `od ${variables.studiosAreaMin} do ${variables.studiosAreaMax} mkw.`;
    const isAreaMaxOver35 = variables.studiosAreaMax > 35;

    return [
        {
            headline: `FAQ Kawalerki ${variables.regionName} - dane RynekPierwotny.pl`,
            paragraphs: []
        },
        {
            headline: `Ile kawalerek jest dostępnych w sprzedaży ${variables.regionNameDeclensionWhere}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: `Aktualnie na rynekpierwotny.pl ${variables.regionNameDeclensionWhere} ${pluralize([
                        "dostępna jest",
                        "dostępne są",
                        "dostępnych jest"
                    ])(variables.studiosPropertiesCountForSale)} ${numberFormat(variables.studiosPropertiesCountForSale)} ${pluralize([
                        "oferta",
                        "oferty",
                        "ofert"
                    ])(
                        variables.studiosPropertiesCountForSale
                    )} kawalerek. Są one rozmieszczone w ${numberFormat(variables.studiosOffersCountForSale)} ${pluralize([
                        "inwestycji deweloperskiej",
                        "inwestycjach deweloperskich",
                        "inwestycjach deweloperskich"
                    ])(
                        variables.studiosOffersCountForSale
                    )}. Kawalerki stanowią ${studiosPercentage} proc. całej oferty nowych mieszkań ${variables.regionNameDeclensionWhere}.`
                }
            ]
        },
        {
            headline: `Ile kosztuje kawalerka ${variables.regionNameDeclensionWhere} w ${currentYear} roku?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: [
                        variables.studiosRangesPriceM2Avg > 0 &&
                            `Średnia cena za metr kwadratowy kawalerki wynosi obecnie ${priceFormat(variables.studiosRangesPriceM2Avg)}.`,
                        variables.studioPriceMin > 0 &&
                            ` Najtańsza kawalerka na rynku pierwotnym ${variables.regionNameDeclensionWhere} kosztuje w ${currentMonth} ${currentYear} - ${priceFormat(variables.studioPriceMin)}.`
                    ].filter(Boolean) as string[]
                },
                {
                    type: ParagraphType.TEXT,
                    text: `Ceny różnią się istotnie w zależności od lokalizacji, piętra i standardu. W menu rozwijanym "Sortuj" na liście ofert wybierz opcję "Najtańsze", aby uporządkować oferty wg najniższej ceny.`
                }
            ]
        },
        {
            headline: `Czy deweloperzy oferują promocje na kawalerki?`,
            paragraphs: [
                variables.promotionsCount > 0
                    ? {
                          type: ParagraphType.TEXT,
                          text: `Obecnie deweloperzy oferują ${numberFormat(variables.promotionsCount)} ${pluralize([
                              "ofertę promocyjną",
                              "oferty promocyjne",
                              "ofert promocyjnych"
                          ])(
                              variables.promotionsCount
                          )} na zakup kawalerki ${variables.regionNameDeclensionWhere}. Warto zapytać o szczegóły przy konkretnej inwestycji.`
                      }
                    : {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach ${variables.regionNameDeclensionWhere} nie obowiązują promocje na kawalerki. Zapisz się na newsletter, żeby otrzymać powiadomienie, gdy się to zmieni.`
                      }
            ]
        },
        {
            headline: `Kiedy będzie można odebrać klucze do nowej kawalerki?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: `Dostępność kawalerek w ${currentMonth} ${currentYear} ${variables.regionNameDeclensionWhere} wygląda następująco:`
                },
                {
                    type: ParagraphType.LIST,
                    text: [
                        `gotowe do odbioru: ${numberFormat(variables.studiosCountReady)} ${pluralize(["mieszkanie", "mieszkania", "mieszkań"])(variables.studiosCountReady)}`,
                        variables.studiosCountReadyInThreeMonths > 0 &&
                            `odbiór w ciągu 3 miesięcy: ${numberFormat(variables.studiosCountReadyInThreeMonths)} ${pluralize(["mieszkanie", "mieszkania", "mieszkań"])(variables.studiosCountReadyInThreeMonths)}`,
                        variables.studiosCountReadyInSixMonths > 0 &&
                            `odbiór w ciągu 6 miesięcy: ${numberFormat(variables.studiosCountReadyInSixMonths)} ${pluralize(["mieszkanie", "mieszkania", "mieszkań"])(variables.studiosCountReadyInSixMonths)}`,
                        variables.studiosCountReadyInOneYear > 0 &&
                            `odbiór w ciągu roku: ${numberFormat(variables.studiosCountReadyInOneYear)} ${pluralize(["mieszkanie", "mieszkania", "mieszkań"])(variables.studiosCountReadyInOneYear)}`
                    ].filter(Boolean) as string[]
                },
                {
                    type: ParagraphType.TEXT,
                    text: `Filtruj oferty korzystając z opcji „termin oddania".`
                }
            ]
        },
        ...(variables.studiosGroundFloor > 0 || variables.studiosLowFloor > 0 || variables.studiosHighFloor > 0 || variables.studiosLastFloor > 0
            ? ([
                  {
                      headline: `Jakie kawalerki są dostępne na portalu?`,
                      paragraphs: [
                          {
                              type: ParagraphType.TEXT,
                              text: isAreaMaxOver35
                                  ? `Deweloperzy mają w sprzedaży kawalerki o powierzchni od ${variables.studiosAreaMin} mkw. Metraż mieszkań typu studio wynosi do ${variables.studiosAreaMax} mkw.`
                                  : `Deweloperzy mają w sprzedaży kawalerki o powierzchni ${areaText}`
                          },
                          {
                              type: ParagraphType.TEXT,
                              text: `Używając menu "Filtry" na liście ofert i wskazując opcję "Piętra" możesz wybierać spośród:`
                          },
                          {
                              type: ParagraphType.LIST,
                              text: [
                                  variables.studiosGroundFloor > 0 &&
                                      `kawalerek na parterze: ${numberFormat(variables.studiosGroundFloor)} ${pluralize(["oferta", "oferty", "ofert"])(variables.studiosGroundFloor)}${variables.studiosGroundFloorWithGarden > 0 ? ` (w tym ${variables.studiosGroundFloorWithGarden} z ogródkiem)` : ""}`,
                                  variables.studiosLowFloor > 0 &&
                                      `kawalerek na niższych piętrach (1-4): ${numberFormat(variables.studiosLowFloor)} ${pluralize(["oferta", "oferty", "ofert"])(variables.studiosLowFloor)}`,
                                  variables.studiosHighFloor > 0 &&
                                      `kawalerek na wysokich piętrach (5+): ${numberFormat(variables.studiosHighFloor)} ${pluralize(["oferta", "oferty", "ofert"])(variables.studiosHighFloor)}`,
                                  variables.studiosLastFloor > 0 &&
                                      `kawalerek na ostatnim piętrze: ${numberFormat(variables.studiosLastFloor)} ${pluralize(["oferta", "oferty", "ofert"])(variables.studiosLastFloor)}`
                              ].filter(Boolean) as string[]
                          }
                      ]
                  }
              ] as ILocationGenericSection[])
            : [])
    ];
};
import {numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {MONTH_POLISH_NAMES} from "../../types/month_polish_names";
import {ILocationGenericSection, ILocationGenericSectionProps, ParagraphType} from "./constants";

export const studioOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const now = new Date();
    const currentMonth = MONTH_POLISH_NAMES[now.getMonth()];
    const currentYear = now.getFullYear();

    const variables = {
        regionName: props.region.name,
        regionNameDeclensionWhere: props.region.name_declension_where,
        regionPropertiesCountForSale: props.region.stats?.properties_all_count_for_sale || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        studioPriceMin: props.region.stats?.properties_studios_ranges_price_min || 0,
        studiosRangesPriceM2Avg: props.region.stats?.properties_studios_ranges_price_m2_avg || 0,
        studiosCountReady: props.region.stats?.properties_studios_count_ready || 0,
        studiosCountReadyInThreeMonths: props.region.stats?.properties_studios_count_ready_in_3m || 0,
        studiosCountReadyInSixMonths: props.region.stats?.properties_studios_count_ready_in_6m || 0,
        studiosCountReadyInOneYear: props.region.stats?.properties_studios_count_ready_in_12m || 0,
        studiosGroundFloor: props.region.stats?.properties_studios_ground_floor || 0,
        studiosGroundFloorWithGarden: props.region.stats?.properties_studios_ground_floor_with_garden || 0,
        studiosHighFloor: props.region.stats?.properties_studios_high_floor || 0,
        studiosLastFloor: props.region.stats?.properties_studios_last_floor || 0,
        studiosLowFloor: props.region.stats?.properties_studios_low_floor || 0,
        studiosOffersCountForSale: props.region.stats?.offers_studios_count_for_sale || 0,
        studiosPropertiesCountForSale: props.region.stats?.properties_studios_count_for_sale || 0,
        studiosAreaMin: props.region.stats?.properties_studios_ranges_area_min || 0,
        studiosAreaMax: props.region.stats?.properties_studios_ranges_area_max || 0
    };

    if (!variables.studiosOffersCountForSale || !variables.studiosPropertiesCountForSale) {
        return [];
    }

    const studiosPercentage =
        variables.regionPropertiesCountForSale > 0 ? Math.round((variables.studiosPropertiesCountForSale / variables.regionPropertiesCountForSale) * 100) : 0;

    const areaText =
        variables.studiosAreaMin === variables.studiosAreaMax || variables.studiosAreaMax === 0
            ? `${variables.studiosAreaMin} mkw.`
            : `od ${variables.studiosAreaMin} do ${variables.studiosAreaMax} mkw.`;
    const isAreaMaxOver35 = variables.studiosAreaMax > 35;

    return [
        {
            headline: `FAQ Kawalerki ${variables.regionName} - dane RynekPierwotny.pl`,
            paragraphs: []
        },
        {
            headline: `Ile kawalerek jest dostępnych w sprzedaży ${variables.regionNameDeclensionWhere}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: `Aktualnie na rynekpierwotny.pl ${variables.regionNameDeclensionWhere} ${pluralize([
                        "dostępna jest",
                        "dostępne są",
                        "dostępnych jest"
                    ])(variables.studiosPropertiesCountForSale)} ${numberFormat(variables.studiosPropertiesCountForSale)} ${pluralize([
                        "oferta",
                        "oferty",
                        "ofert"
                    ])(
                        variables.studiosPropertiesCountForSale
                    )} kawalerek. Są one rozmieszczone w ${numberFormat(variables.studiosOffersCountForSale)} ${pluralize([
                        "inwestycji deweloperskiej",
                        "inwestycjach deweloperskich",
                        "inwestycjach deweloperskich"
                    ])(
                        variables.studiosOffersCountForSale
                    )}. Kawalerki stanowią ${studiosPercentage} proc. całej oferty nowych mieszkań ${variables.regionNameDeclensionWhere}.`
                }
            ]
        },
        {
            headline: `Ile kosztuje kawalerka ${variables.regionNameDeclensionWhere} w ${currentYear} roku?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: [
                        variables.studiosRangesPriceM2Avg > 0 &&
                            `Średnia cena za metr kwadratowy kawalerki wynosi obecnie ${priceFormat(variables.studiosRangesPriceM2Avg)}.`,
                        variables.studioPriceMin > 0 &&
                            ` Najtańsza kawalerka na rynku pierwotnym ${variables.regionNameDeclensionWhere} kosztuje w ${currentMonth} ${currentYear} - ${priceFormat(variables.studioPriceMin)}.`
                    ].filter(Boolean) as string[]
                },
                {
                    type: ParagraphType.TEXT,
                    text: `Ceny różnią się istotnie w zależności od lokalizacji, piętra i standardu. W menu rozwijanym "Sortuj" na liście ofert wybierz opcję "Najtańsze", aby uporządkować oferty wg najniższej ceny.`
                }
            ]
        },
        {
            headline: `Czy deweloperzy oferują promocje na kawalerki?`,
            paragraphs: [
                variables.promotionsCount > 0
                    ? {
                          type: ParagraphType.TEXT,
                          text: `Obecnie deweloperzy oferują ${numberFormat(variables.promotionsCount)} ${pluralize([
                              "ofertę promocyjną",
                              "oferty promocyjne",
                              "ofert promocyjnych"
                          ])(
                              variables.promotionsCount
                          )} na zakup kawalerki ${variables.regionNameDeclensionWhere}. Warto zapytać o szczegóły przy konkretnej inwestycji.`
                      }
                    : {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach ${variables.regionNameDeclensionWhere} nie obowiązują promocje na kawalerki. Zapisz się na newsletter, żeby otrzymać powiadomienie, gdy się to zmieni.`
                      }
            ]
        },
        {
            headline: `Kiedy będzie można odebrać klucze do nowej kawalerki?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: `Dostępność kawalerek w ${currentMonth} ${currentYear} ${variables.regionNameDeclensionWhere} wygląda następująco:`
                },
                {
                    type: ParagraphType.LIST,
                    text: [
                        `gotowe do odbioru: ${numberFormat(variables.studiosCountReady)} ${pluralize(["mieszkanie", "mieszkania", "mieszkań"])(variables.studiosCountReady)}`,
                        variables.studiosCountReadyInThreeMonths > 0 &&
                            `odbiór w ciągu 3 miesięcy: ${numberFormat(variables.studiosCountReadyInThreeMonths)} ${pluralize(["mieszkanie", "mieszkania", "mieszkań"])(variables.studiosCountReadyInThreeMonths)}`,
                        variables.studiosCountReadyInSixMonths > 0 &&
                            `odbiór w ciągu 6 miesięcy: ${numberFormat(variables.studiosCountReadyInSixMonths)} ${pluralize(["mieszkanie", "mieszkania", "mieszkań"])(variables.studiosCountReadyInSixMonths)}`,
                        variables.studiosCountReadyInOneYear > 0 &&
                            `odbiór w ciągu roku: ${numberFormat(variables.studiosCountReadyInOneYear)} ${pluralize(["mieszkanie", "mieszkania", "mieszkań"])(variables.studiosCountReadyInOneYear)}`
                    ].filter(Boolean) as string[]
                },
                {
                    type: ParagraphType.TEXT,
                    text: `Filtruj oferty korzystając z opcji „termin oddania".`
                }
            ]
        },
        ...(variables.studiosGroundFloor > 0 || variables.studiosLowFloor > 0 || variables.studiosHighFloor > 0 || variables.studiosLastFloor > 0
            ? ([
                  {
                      headline: `Jakie kawalerki są dostępne na portalu?`,
                      paragraphs: [
                          {
                              type: ParagraphType.TEXT,
                              text: isAreaMaxOver35
                                  ? `Deweloperzy mają w sprzedaży kawalerki o powierzchni od ${variables.studiosAreaMin} mkw. Metraż mieszkań typu studio wynosi do ${variables.studiosAreaMax} mkw.`
                                  : `Deweloperzy mają w sprzedaży kawalerki o powierzchni ${areaText}`
                          },
                          {
                              type: ParagraphType.TEXT,
                              text: `Używając menu "Filtry" na liście ofert i wskazując opcję "Piętra" możesz wybierać spośród:`
                          },
                          {
                              type: ParagraphType.LIST,
                              text: [
                                  variables.studiosGroundFloor > 0 &&
                                      `kawalerek na parterze: ${numberFormat(variables.studiosGroundFloor)} ${pluralize(["oferta", "oferty", "ofert"])(variables.studiosGroundFloor)}${variables.studiosGroundFloorWithGarden > 0 ? ` (w tym ${variables.studiosGroundFloorWithGarden} z ogródkiem)` : ""}`,
                                  variables.studiosLowFloor > 0 &&
                                      `kawalerek na niższych piętrach (1-4): ${numberFormat(variables.studiosLowFloor)} ${pluralize(["oferta", "oferty", "ofert"])(variables.studiosLowFloor)}`,
                                  variables.studiosHighFloor > 0 &&
                                      `kawalerek na wysokich piętrach (5+): ${numberFormat(variables.studiosHighFloor)} ${pluralize(["oferta", "oferty", "ofert"])(variables.studiosHighFloor)}`,
                                  variables.studiosLastFloor > 0 &&
                                      `kawalerek na ostatnim piętrze: ${numberFormat(variables.studiosLastFloor)} ${pluralize(["oferta", "oferty", "ofert"])(variables.studiosLastFloor)}`
                              ].filter(Boolean) as string[]
                          }
                      ]
                  }
              ] as ILocationGenericSection[])
            : [])
    ];
};
